import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import {
  clientDetailPath,
  clientEditPath,
  goToClientDetail,
} from './client-route.util';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, tap } from 'rxjs';
import type { Observable } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  TenantOnboardingApiService,
  TenantRow,
  TenantDeletedRow,
  TenantListQuery,
} from 'src/app/core/services/tenant-onboarding-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleSortState,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { ManageClientTableComponent } from './manage-client-table/manage-client-table.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import {
  DeleteConfirmModalComponent,
  type DeleteConfirmVariant,
} from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import {
  ConfirmActionDialogComponent,
  type ConfirmActionDialogData,
} from './confirm-action-dialog.component';
import { APP_DATETIME_FORMAT } from 'src/app/shared/pipes/app-date.pipe';
import { formatPhoneInternationalDisplay } from 'src/app/shared/utils/international-phone';
import { downloadExcelFromJson } from 'src/app/shared/utils/excel.util';
import type { PageSurfaceTab } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';

export type ClientListView = 'active' | 'deleted';

@Component({
  selector: 'app-manage-client',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    MatDialogModule,
    NgApexchartsModule,
    IconModule,
    NgScrollbarModule,
    ListModuleSurfaceComponent,
    ManageClientTableComponent,
    HasPermissionPipe,
    RestoreConfirmModalComponent,
    DeleteConfirmModalComponent,
  ],
  templateUrl: './manage-client.component.html',
  styleUrl: './manage-client.component.css',
})
export class ManageClientComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search name, email, subdomain…',
        deletedPlaceholder: 'Search deleted clients…',
      },
      status: {
        options: [
          { value: '', label: 'All status' },
          { value: 'Active', label: 'Active' },
          { value: 'InActive', label: 'Inactive' },
          { value: 'Blocked', label: 'Blocked' },
          { value: 'Suspended', label: 'Suspended' },
          { value: 'Locked', label: 'Locked' },
        ],
      },
      dateRange: {
        placeholder: 'Created date range',
        deletedPlaceholder: 'Deleted date range',
      },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  /** Toggle between onboarded clients and soft-deleted (trash). */
  view: ClientListView = 'active';

  readonly clientSurfaceTabs: PageSurfaceTab[] = [
    { key: 'active', label: 'Active' },
    { key: 'deleted', label: 'Deleted' },
  ];

  tenants: TenantRow[] = [];
  deletedRows: TenantDeletedRow[] = [];

  loading = false;
  /** True while assembling all filtered pages for Excel export. */
  exporting = false;
  errorMessage = '';

  page = 1;
  readonly pageSize = 10;
  meta: { total: number; lastPage: number; currentPage: number; perPage: number } | null = null;

  /** Same row-action pattern as manage-agent (`active-0`, `deleted-1`, …). */
  openActionMenuKey: string | null = null;

  showRestoreModal = false;
  restoreLoading = signal(false);
  selectedDeletedClient: TenantDeletedRow | null = null;

  showDeleteModal = false;
  deleteLoading = signal(false);
  deleteError = '';
  deleteVariant: DeleteConfirmVariant = 'soft-delete';
  selectedClientForDelete: TenantRow | TenantDeletedRow | null = null;

  get selectedDeletedClientName(): string {
    const row = this.selectedDeletedClient;
    if (!row) return '';
    return row.tenant_name ?? row.tenant_subdomain ?? row.unique_id ?? '';
  }

  get selectedClientDeleteName(): string {
    const row = this.selectedClientForDelete;
    if (!row) return '';
    return row.tenant_name ?? row.tenant_subdomain ?? row.unique_id ?? '';
  }

  get deleteModalHeading(): string {
    return this.deleteVariant === 'permanent' ? 'Remove client permanently?' : 'Remove client?';
  }

  get deleteModalMessage(): string {
    const name = this.selectedClientDeleteName;
    if (this.deleteVariant === 'permanent') {
      return `This will permanently delete ${name} from the platform and drop its tenant database. This cannot be undone.`;
    }
    return '';
  }

  get deleteModalConfirmLabel(): string {
    return this.deleteVariant === 'permanent' ? 'Delete permanently' : 'Remove';
  }

  get deleteModalLoadingLabel(): string {
    return this.deleteVariant === 'permanent' ? 'Deleting…' : 'Removing…';
  }

  ngOnInit(): void {
    this.loadTenants();
  }

  setView(mode: ClientListView): void {
    if (this.view === mode) {
      return;
    }
    if (mode === 'deleted' && !this.permissionService.isAccessAllowed(this.P.TENANT_ARCHIVED_LIST)) {
      this.snackBar.open('You do not have permission to view deleted clients.', 'Close', {
        duration: 5000,
      });
      return;
    }
    if (mode === 'active' && !this.permissionService.isAccessAllowed(this.P.TENANT_LIST)) {
      this.snackBar.open('You do not have permission to view active clients.', 'Close', {
        duration: 5000,
      });
      return;
    }
    this.closeActionMenu();
    this.closeRestoreModal();
    this.view = mode;
    this.page = 1;
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    this.sortColumn = mode === 'deleted' ? 'deleted_at' : 'id';
    this.sortBy = 'desc';
    this.loadTenants();
  }

  onClientTabKey(key: string): void {
    if (key === 'active' || key === 'deleted') {
      this.setView(key);
    }
  }

  /** Must match backend `TenantService.TENANT_LIST_SORT_COLUMNS`. */
  sortColumn = 'id';
  sortBy: 'asc' | 'desc' = 'desc';

  get emptyMessage(): string {
    return this.view === 'deleted'
      ? 'No deleted clients match your filters.'
      : 'No clients match your filters.';
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.loadTenants();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.loadTenants();
  }

  onTableSort(state: ListModuleSortState): void {
    const column = state.column;
    const allowedActive = new Set([
      'id',
      'unique_id',
      'tenant_name',
      'contact_person',
      'contact_number',
      'email',
      'status',
      'created_at',
      'updated_at',
    ]);
    const allowedDeleted = new Set([...allowedActive, 'deleted_at']);
    const allowed = this.view === 'deleted' ? allowedDeleted : allowedActive;
    if (!allowed.has(column)) return;
    this.sortColumn = column;
    this.sortBy = state.direction;
    this.page = 1;
    this.loadTenants();
  }

  menuEdit(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    const path = clientEditPath(row);
    if (path) void this.router.navigate(path);
  }

  menuToggleStatus(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.toggleStatus(row);
  }

  /** Flip Active ↔ InActive (same pattern as manage admin users). */
  toggleStatus(tenant: TenantRow): void {
    const nextStatus: 'Active' | 'InActive' =
      tenant.status === 'Active' ? 'InActive' : 'Active';
    this.tenantApi.toggleTenantStatus(tenant.id, nextStatus).subscribe({
      next: (res) => {
        if (res?.data?.status) {
          tenant.status = res.data.status;
        } else {
          tenant.status = nextStatus;
        }
        this.snackBar.open(
          res?.message ||
            (tenant.status === 'Active' ? 'Client activated.' : 'Client deactivated.'),
          'Dismiss',
          { duration: 4000 },
        );
      },
      error: (err) => {
        this.snackBar.open(
          err?.error?.message || err?.message || 'Could not update status.',
          'Dismiss',
          { duration: 5000 },
        );
      },
    });
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  /** Menu row handlers close the dropdown then delegate (typed; `displayRows` is a union). */
  menuDelete(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.confirmDelete(row);
  }

  menuResend(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.openResendPasswordLink(row);
  }

  menuApplyMigration(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.openApplyMigration(row);
  }

  menuApplySeeder(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.openApplySeeder(row);
  }

  menuRestore(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'deleted') return;
    this.confirmRestore(row);
  }

  confirmRestore(row: TenantDeletedRow): void {
    this.selectedDeletedClient = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedDeletedClient = null;
  }

  submitRestore(): void {
    if (!this.selectedDeletedClient) return;
    this.runRestoreTenant(this.selectedDeletedClient);
  }

  menuPermanentDelete(row: TenantRow | TenantDeletedRow): void {
    this.closeActionMenu();
    if (this.view !== 'deleted') return;
    this.confirmPermanentDelete(row);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (t.closest('.agent-action-menu')) {
      return;
    }
    this.openActionMenuKey = null;
  }

  /** Rows currently shown in the table (typed union for template). */
  get displayRows(): Array<TenantRow | TenantDeletedRow> {
    return this.view === 'deleted' ? this.deletedRows : this.tenants;
  }

  loadTenants(): void {
    this.loading = true;
    this.errorMessage = '';
    const query = {
      page: this.page,
      page_size: this.pageSize,
      searchText: this.listFilters.searchQuery.trim() || undefined,
      sortColumn: this.sortColumn,
      sortBy: this.sortBy,
      status: this.listFilters.status.trim() || undefined,
      createdFrom: this.listFilters.dateRangeStart || undefined,
      createdTo: this.listFilters.dateRangeEnd || undefined,
    };

    const req$ =
      this.view === 'deleted'
        ? this.tenantApi.listDeletedTenants(query)
        : this.tenantApi.listTenants(query);

    req$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: (res) => {
        if (!res?.status || !res.data) {
          this.errorMessage = res?.message || 'Could not load clients.';
          this.tenants = [];
          this.deletedRows = [];
          this.meta = null;
          return;
        }
        const payload = res.data;
        if (this.view === 'deleted') {
          this.deletedRows = payload.result ?? [];
          this.tenants = [];
        } else {
          this.tenants = payload.result ?? [];
          this.deletedRows = [];
        }
        this.meta = payload.meta ?? null;
      },
      error: (err: unknown) => {
        this.tenants = [];
        this.deletedRows = [];
        this.meta = null;
        this.errorMessage = this.parseHttpError(err);
      },
    });
  }

  private parseHttpError(err: unknown): string {
    const resolved = resolveApiErrorMessage(err);
    if (resolved) {
      return resolved;
    }
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as Record<string, unknown>)['message'];
        if (typeof m === 'string') {
          return m;
        }
        if (Array.isArray(m)) {
          return m.map(String).join(' ');
        }
      }
      if (err.status === 401) {
        return 'Please sign in again.';
      }
      if (err.status === 0) {
        return 'Cannot reach the API. Check that the backend is running.';
      }
    }
    return 'Failed to load clients.';
  }

  confirmDelete(row: TenantRow): void {
    this.closeActionMenu();
    this.deleteError = '';
    this.deleteVariant = 'soft-delete';
    this.selectedClientForDelete = row;
    this.showDeleteModal = true;
  }

  confirmPermanentDelete(row: TenantDeletedRow): void {
    this.deleteError = '';
    this.deleteVariant = 'permanent';
    this.selectedClientForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedClientForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedClientForDelete;
    if (!row) return;
    if (this.deleteVariant === 'permanent') {
      this.runPermanentDelete(row);
    } else {
      this.runSoftDelete(row);
    }
  }

  private runSoftDelete(row: TenantRow): void {
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.tenantApi
      .deleteTenant(row.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status === true || res?.statusCode === 200) {
            this.showSuccessToast(this.successMessageFromApi(res, 'Client removed successfully.'));
            this.closeDeleteModal();
            if (this.tenants.length <= 1 && this.page > 1) {
              this.page -= 1;
            }
            this.loadTenants();
            return;
          }
          this.deleteError = res?.message || 'Delete failed.';
        },
        error: (err: unknown) => {
          this.deleteError = this.parseHttpError(err);
        },
      });
  }

  private runRestoreTenant(row: TenantDeletedRow): void {
    this.restoreLoading.set(true);
    this.tenantApi
      .restoreTenant(row.id)
      .pipe(finalize(() => this.restoreLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status === true || res?.statusCode === 200) {
            this.showSuccessToast(this.successMessageFromApi(res, 'Client restored successfully.'));
            this.closeRestoreModal();
            if (this.deletedRows.length <= 1 && this.page > 1) {
              this.page -= 1;
            }
            this.loadTenants();
            return;
          }
          alert(res?.message || 'Restore failed.');
          this.closeRestoreModal();
        },
        error: (err: unknown) => {
          alert(this.parseHttpError(err));
          this.closeRestoreModal();
        },
      });
  }

  /** Resend welcome / reset link (active tenants only). */
  openResendPasswordLink(row: TenantRow): void {
    const name = row.tenant_name ?? row.tenant_subdomain ?? row.unique_id;
    this.confirmTenantSideEffect(row, {
      title: 'Resend link?',
      message: `Send the welcome / password setup email again for ${name}?`,
      confirmLabel: 'Resend link',
      confirmColor: 'primary',
    }, (r) => this.executeResendPasswordLink(r));
  }

  private executeResendPasswordLink(row: TenantRow): Observable<unknown> {
    return this.tenantApi.resendTenantPasswordLink(row.id).pipe(
      tap((res) => {
        this.showSuccessToast(this.successMessageFromApi(res, 'Email sent successfully.'));
      }),
    );
  }

  /** Apply pending Prisma migrations to tenant DB schemas. */
  openApplyMigration(row: TenantRow): void {
    const name = row.tenant_name ?? row.tenant_subdomain ?? row.unique_id;
    this.confirmTenantSideEffect(row, {
      title: 'Apply migration?',
      message: `Apply pending migrations to ${name}'s database (agenticpd_schema and sample_schema)? This may take a minute.`,
      confirmLabel: 'Apply migration',
      confirmColor: 'primary',
    }, (r) => this.executeApplyMigration(r));
  }

  private executeApplyMigration(row: TenantRow): Observable<unknown> {
    return this.tenantApi.applyTenantMigration(row.id).pipe(
      tap((res) => {
        this.showSuccessToast(this.successMessageFromApi(res, 'Migrations applied.'));
      }),
    );
  }

  /** Run tenant seeders / bootstrap hooks. */
  openApplySeeder(row: TenantRow): void {
    const name = row.tenant_name ?? row.tenant_subdomain ?? row.unique_id;
    this.confirmTenantSideEffect(row, {
      title: 'Apply seeder?',
      message: `Run reference seeders and bootstrap data for ${name}? This may take a minute.`,
      confirmLabel: 'Apply seeder',
      confirmColor: 'primary',
    }, (r) => this.executeApplySeeder(r));
  }

  private executeApplySeeder(row: TenantRow): Observable<unknown> {
    return this.tenantApi.applyTenantSeeder(row.id).pipe(
      tap((res) => {
        this.showSuccessToast(this.successMessageFromApi(res, 'Seeders applied.'));
      }),
    );
  }

  private confirmTenantSideEffect(
    row: TenantRow,
    dialogData: ConfirmActionDialogData,
    run: (r: TenantRow) => Observable<unknown>,
  ): void {
    this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      data: {
        ...dialogData,
        loadingLabel: 'Working…',
        action: () => run(row),
      },
      disableClose: true,
    });
  }

  private runPermanentDelete(row: TenantDeletedRow): void {
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.tenantApi
      .permanentDeleteTenant(row.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status === true || res?.statusCode === 200) {
            this.showSuccessToast(
              this.successMessageFromApi(
                res,
                'Client permanently removed. The client database was dropped.',
              ),
            );
            this.closeDeleteModal();
            if (this.deletedRows.length <= 1 && this.page > 1) {
              this.page -= 1;
            }
            this.loadTenants();
            return;
          }
          this.deleteError = res?.message || 'Permanent delete failed.';
        },
        error: (err: unknown) => {
          this.deleteError = this.parseHttpError(err);
        },
      });
  }

  /** Prefer API envelope message when present and non-empty. */
  private successMessageFromApi(res: { message?: unknown }, fallback: string): string {
    const m = res?.message;
    if (typeof m === 'string' && m.trim()) {
      return m.trim();
    }
    return fallback;
  }

  /** Material snack bar (top-right) for successful delete/restore actions. */
  private showSuccessToast(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  private showErrorToast(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 8000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['app-snackbar', 'app-snackbar-error'],
    });
  }

  /** Downloads an `.xlsx` of every row matching the current filters, sort, and active/deleted tab. */
  exportExcel(): void {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this.tenantApi
      .fetchAllTenantRows(this.exportListQuery(500), this.view === 'deleted')
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: (rows) => {
          if (!rows.length) {
            this.snackBar.open('No rows match the current filters.', 'Dismiss', {
              duration: 4000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
            });
            return;
          }
          const data = this.buildExcelExportRows(rows);
          const sheetLabel =
            this.view === 'deleted' ? 'Deleted clients' : 'Clients';
          const stamp = new Date().toISOString().slice(0, 10);
          const segment = this.view === 'deleted' ? 'deleted' : 'active';
          void downloadExcelFromJson(
            data,
            sheetLabel,
            `clients-export-${segment}-${stamp}.xlsx`,
          ).then(() => {
            this.showSuccessToast(
              `Exported ${rows.length} row${rows.length === 1 ? '' : 's'}.`,
            );
          });
        },
        error: (err: unknown) => {
          this.snackBar.open(this.parseHttpError(err), 'Dismiss', {
            duration: 6000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
  }

  private exportListQuery(chunkSize: number): Omit<TenantListQuery, 'page'> {
    return {
      page_size: chunkSize,
      searchText: this.listFilters.searchQuery.trim() || undefined,
      sortColumn: this.sortColumn,
      sortBy: this.sortBy,
      status: this.listFilters.status.trim() || undefined,
      createdFrom: this.listFilters.dateRangeStart || undefined,
      createdTo: this.listFilters.dateRangeEnd || undefined,
    };
  }

  private buildExcelExportRows(
    rows: Array<TenantRow | TenantDeletedRow>,
  ): Record<string, string | number>[] {
    const deleted = this.view === 'deleted';
    return rows.map((row) => {
      const display = formatPhoneInternationalDisplay(
        row.contact_number,
        row.contact_number_country_code ?? null,
        row.contact_number_country_iso ?? null,
      );
      const phone = display === '—' ? '' : display;
      const record: Record<string, string | number> = {
        'Reference ID': row.unique_id ?? '',
        'Client name': row.tenant_name ?? '',
        Subdomain: row.tenant_subdomain ?? '',
        'Contact person': row.contact_person ?? '',
        Phone: phone,
        Email: row.email ?? '',
        Status: row.status ?? '',
        'Created (UTC)': this.formatExportCellDate(row.created_at),
      };
      if (deleted) {
        const deletedAt = 'deleted_at' in row ? row.deleted_at : undefined;
        record['Deleted (UTC)'] = this.formatExportCellDate(deletedAt);
      }
      return record;
    });
  }

  private formatExportCellDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return formatDate(d, APP_DATETIME_FORMAT, 'en-GB');
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    if (!n) {
      return '?';
    }
    return n.charAt(0).toUpperCase();
  }

  clientDetailRoute(row: TenantRow | TenantDeletedRow): string[] | null {
    return clientDetailPath(row);
  }

  goToClientDetail(row: TenantRow | TenantDeletedRow): void {
    if (this.view !== 'active') return;
    goToClientDetail(this.router, row, (message) =>
      this.snackBar.open(message, 'Dismiss', { duration: 5000 }),
    );
  }

  clientEditRoute(row: TenantRow): string[] | null {
    return clientEditPath(row);
  }

  statusBadgeClass(status: string): Record<string, boolean> {
    return {
      'text-success bg-success-light': status === 'Active',
      'text-secondary bg-secondary-light': status === 'InActive',
      'text-danger bg-danger-light': status === 'Blocked',
      'text-warning bg-warning-light': status === 'Suspended' || status === 'Locked',
    };
  }

}
